import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '@/layouts/admin-layout'
import { AppHeader } from '@/components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Link, router } from '@inertiajs/react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { CheckIcon, XIcon, PencilIcon } from 'lucide-react'

interface AgentSkill {
  id: number
  slug: string
  name: string
  description: string
  content: string
  skill_type: string
  status: string
  submission_count: number
  usage_count: number
  applicable_contexts: string[]
  reviewed_by: string | null
  reviewed_at: string | null
  rejection_reason: string | null
  created_at: string
}

interface SkillSubmission {
  id: number
  slug: string
  name: string
  project_name: string | null
  created_at: string
}

interface AdminAgentSkillShowProps {
  skill: AgentSkill
  submissions: SkillSubmission[]
  valid_contexts: string[]
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
  }
}

function Show({ skill, submissions, valid_contexts }: AdminAgentSkillShowProps) {
  const { t } = useTranslation('admin')
  const [isApproving, setIsApproving] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')

  // Edit form state
  const [editName, setEditName] = useState(skill.name)
  const [editDescription, setEditDescription] = useState(skill.description)
  const [editContent, setEditContent] = useState(skill.content)
  const [editContexts, setEditContexts] = useState<string[]>(skill.applicable_contexts)

  const getStatusBadge = (status: string) => {
    const statusVariants: Record<string, 'warning' | 'success' | 'destructive' | 'secondary'> = {
      'pending': 'warning',
      'active': 'success',
      'rejected': 'destructive',
    }

    return (
      <Badge variant={statusVariants[status] || 'secondary'}>
        {t(`agent_skills.status.${status}`)}
      </Badge>
    )
  }

  const getTypeBadge = (skillType: string) => {
    return (
      <Badge variant="secondary" className="text-xs">
        {t(`agent_skills.types.${skillType}`)}
      </Badge>
    )
  }

  const handleApprove = () => {
    setIsApproving(true)
    router.post(`/admin/agent_skills/${skill.id}/approve`, {}, {
      onFinish: () => setIsApproving(false)
    })
  }

  const handleApproveWithEdits = () => {
    setIsApproving(true)
    router.post(`/admin/agent_skills/${skill.id}/approve`, {
      name: editName,
      description: editDescription,
      content: editContent,
      contexts: editContexts
    }, {
      onFinish: () => {
        setIsApproving(false)
        setIsEditDialogOpen(false)
      }
    })
  }

  const handleReject = () => {
    setIsRejecting(true)
    router.post(`/admin/agent_skills/${skill.id}/reject`, {
      reason: rejectionReason
    }, {
      onFinish: () => {
        setIsRejecting(false)
        setRejectDialogOpen(false)
        setRejectionReason('')
      }
    })
  }

  const toggleContext = (context: string) => {
    if (editContexts.includes(context)) {
      setEditContexts(editContexts.filter(c => c !== context))
    } else {
      setEditContexts([...editContexts, context])
    }
  }

  const isPending = skill.status === 'pending'

  return (
    <>
      <AppHeader breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: t('agent_skills.index.title'), href: '/admin/agent_skills' },
        { label: skill.name }
      ]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
              {/* Page Header */}
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-3xl font-bold text-foreground">{skill.name}</h1>
                    {getStatusBadge(skill.status)}
                    {getTypeBadge(skill.skill_type)}
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{skill.description}</p>
                </div>
                <Button variant="outline" asChild>
                  <Link href="/admin/agent_skills">{t('agent_skills.show.backToList')}</Link>
                </Button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Skill Content */}
                <div className="lg:col-span-2 space-y-6">
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>{t('agent_skills.show.content.title')}</CardTitle>
                      <CardDescription>{t('agent_skills.show.content.description')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <pre className="p-4 bg-muted rounded-md overflow-x-auto text-sm whitespace-pre-wrap">
                        {skill.content}
                      </pre>
                    </CardContent>
                  </Card>

                  {/* Source Submissions */}
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>{t('agent_skills.show.submissions.title')}</CardTitle>
                      <CardDescription>
                        {t('agent_skills.show.submissions.description', { count: submissions.length })}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {submissions.length > 0 ? (
                        <div className="divide-y divide-border">
                          {submissions.map((submission) => (
                            <div key={submission.id} className="py-3 flex items-center justify-between">
                              <div>
                                <span className="text-sm font-medium text-foreground">
                                  {submission.name}
                                </span>
                                {submission.project_name && (
                                  <span className="text-sm text-muted-foreground ml-2">
                                    from {submission.project_name}
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {new Date(submission.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {t('agent_skills.show.submissions.empty')}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Actions */}
                  {isPending && (
                    <Card className="shadow-none">
                      <CardHeader>
                        <CardTitle>{t('agent_skills.show.actions.title')}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {/* Quick Approve */}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              className="w-full justify-start bg-green-600 hover:bg-green-700"
                              disabled={isApproving}
                            >
                              <CheckIcon className="mr-2 h-4 w-4" />
                              {t('agent_skills.show.actions.approve')}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t('agent_skills.show.approveDialog.title')}</AlertDialogTitle>
                              <AlertDialogDescription>
                                {t('agent_skills.show.approveDialog.description')}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t('agent_skills.show.approveDialog.cancel')}</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-green-600 hover:bg-green-700"
                                onClick={handleApprove}
                                disabled={isApproving}
                              >
                                {isApproving ? t('agent_skills.show.approveDialog.confirming') : t('agent_skills.show.approveDialog.confirm')}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>

                        {/* Edit & Approve */}
                        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                          <DialogTrigger asChild>
                            <Button variant="outline" className="w-full justify-start">
                              <PencilIcon className="mr-2 h-4 w-4" />
                              {t('agent_skills.show.actions.editAndApprove')}
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>{t('agent_skills.show.editDialog.title')}</DialogTitle>
                              <DialogDescription>
                                {t('agent_skills.show.editDialog.description')}
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div className="space-y-2">
                                <Label htmlFor="name">{t('agent_skills.show.editDialog.name')}</Label>
                                <Input
                                  id="name"
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="description">{t('agent_skills.show.editDialog.description')}</Label>
                                <Textarea
                                  id="description"
                                  value={editDescription}
                                  onChange={(e) => setEditDescription(e.target.value)}
                                  rows={3}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="content">{t('agent_skills.show.editDialog.content')}</Label>
                                <Textarea
                                  id="content"
                                  value={editContent}
                                  onChange={(e) => setEditContent(e.target.value)}
                                  rows={10}
                                  className="font-mono text-sm"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>{t('agent_skills.show.editDialog.contexts')}</Label>
                                <div className="flex flex-wrap gap-2">
                                  {valid_contexts.map((context) => (
                                    <Badge
                                      key={context}
                                      variant={editContexts.includes(context) ? 'default' : 'outline'}
                                      className="cursor-pointer"
                                      onClick={() => toggleContext(context)}
                                    >
                                      {context}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                                {t('agent_skills.show.editDialog.cancel')}
                              </Button>
                              <Button
                                className="bg-green-600 hover:bg-green-700"
                                onClick={handleApproveWithEdits}
                                disabled={isApproving}
                              >
                                {isApproving ? t('agent_skills.show.editDialog.confirming') : t('agent_skills.show.editDialog.confirm')}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>

                        {/* Reject */}
                        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <XIcon className="mr-2 h-4 w-4" />
                              {t('agent_skills.show.actions.reject')}
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>{t('agent_skills.show.rejectDialog.title')}</DialogTitle>
                              <DialogDescription>
                                {t('agent_skills.show.rejectDialog.description')}
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div className="space-y-2">
                                <Label htmlFor="reason">{t('agent_skills.show.rejectDialog.reason')}</Label>
                                <Textarea
                                  id="reason"
                                  value={rejectionReason}
                                  onChange={(e) => setRejectionReason(e.target.value)}
                                  placeholder={t('agent_skills.show.rejectDialog.reasonPlaceholder')}
                                  rows={3}
                                />
                              </div>
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                                {t('agent_skills.show.rejectDialog.cancel')}
                              </Button>
                              <Button
                                variant="destructive"
                                onClick={handleReject}
                                disabled={isRejecting || !rejectionReason.trim()}
                              >
                                {isRejecting ? t('agent_skills.show.rejectDialog.confirming') : t('agent_skills.show.rejectDialog.confirm')}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </CardContent>
                    </Card>
                  )}

                  {/* Skill Info */}
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>{t('agent_skills.show.info.title')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('agent_skills.show.info.slug')}</p>
                        <p className="mt-1 text-sm font-mono text-foreground">{skill.slug}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('agent_skills.show.info.submissions')}</p>
                        <p className="mt-1 text-2xl font-semibold text-foreground">{skill.submission_count}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('agent_skills.show.info.usage')}</p>
                        <p className="mt-1 text-2xl font-semibold text-foreground">{skill.usage_count}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('agent_skills.show.info.created')}</p>
                        <p className="mt-1 text-sm text-foreground">
                          {new Date(skill.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                      {skill.reviewed_by && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">{t('agent_skills.show.info.reviewedBy')}</p>
                          <p className="mt-1 text-sm text-foreground">{skill.reviewed_by}</p>
                          {skill.reviewed_at && (
                            <p className="text-xs text-muted-foreground">
                              {new Date(skill.reviewed_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          )}
                        </div>
                      )}
                      {skill.rejection_reason && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">{t('agent_skills.show.info.rejectionReason')}</p>
                          <p className="mt-1 text-sm text-red-600">{skill.rejection_reason}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Applicable Contexts */}
                  {skill.applicable_contexts.length > 0 && (
                    <Card className="shadow-none">
                      <CardHeader>
                        <CardTitle>{t('agent_skills.show.contexts.title')}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {skill.applicable_contexts.map((context) => (
                            <Badge key={context} variant="secondary">
                              {context}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

Show.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Show
